// Небольшое хранилище уведомлений; доступно и из React, и из API-клиента.
import { useSyncExternalStore } from 'react';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  text: string;
  tone: 'info' | 'error';
  action?: ToastAction;
  /** Уходит: показывается анимация ухода тем же путём, каким пришло, затем удаляется. */
  leaving?: boolean;
}

/** Длительность анимации ухода (совпадает с --dur-2 в global.css). */
export const LEAVE_MS = 200;

interface Timer {
  handle?: number;
  /** Сколько осталось показывать, мс. */
  left: number;
  startedAt: number;
}

let items: ToastItem[] = [];
let seq = 0;
const timers = new Map<number, Timer>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function remove(id: number): void {
  timers.delete(id);
  items = items.filter((x) => x.id !== id);
  emit();
}

function start(id: number): void {
  const tm = timers.get(id);
  if (!tm) return;
  tm.startedAt = Date.now();
  tm.handle = window.setTimeout(() => dismissToast(id), tm.left);
}

export function dismissToast(id: number): void {
  const item = items.find((x) => x.id === id);
  if (!item || item.leaving) return;
  const tm = timers.get(id);
  if (tm?.handle) window.clearTimeout(tm.handle);
  items = items.map((x) => (x.id === id ? { ...x, leaving: true } : x));
  emit();
  window.setTimeout(() => remove(id), LEAVE_MS);
}

/** Курсор на уведомлении — таймер стоит. */
export function pauseToast(id: number): void {
  const tm = timers.get(id);
  if (!tm?.handle) return;
  window.clearTimeout(tm.handle);
  tm.handle = undefined;
  tm.left = Math.max(0, tm.left - (Date.now() - tm.startedAt));
}

/** Курсор ушёл — досчитываем оставшееся время. */
export function resumeToast(id: number): void {
  const tm = timers.get(id);
  if (!tm || tm.handle) return;
  start(id);
}

export function toast(
  text: string,
  opts: { tone?: 'info' | 'error'; action?: ToastAction; ms?: number } = {},
): void {
  // Одинаковый текст не дублируем.
  if (items.some((x) => x.text === text && !x.leaving)) return;
  const item: ToastItem = { id: ++seq, text, tone: opts.tone ?? 'info', action: opts.action };
  const visible = items.filter((x) => !x.leaving);
  visible.slice(0, -2).forEach((x) => dismissToast(x.id));
  items = [...items, item];
  timers.set(item.id, { left: opts.ms ?? (opts.action ? 8000 : 3200), startedAt: 0 });
  start(item.id);
  emit();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export const toastsSnapshot = (): ToastItem[] => items;
export const useToasts = (): ToastItem[] => useSyncExternalStore(subscribe, toastsSnapshot);
